import type { StructureSetId } from '~/domain/changes/ids';
import type { DatasetEnvironmentRegistry } from '~/engine/scene/DatasetEnvironmentRegistry';
import { enqueueSnackbar } from '~/utils/notifications';

/** The fields that identify a structure set and name it to the user. */
export interface LabelledStructureSet {
    readonly id: StructureSetId;
    readonly structureSetLabel: string;
}

/**
 * The message shown when an action is refused because structure sets lost
 * contour edits to a failed switch-away flush. Names each set by its label.
 */
function lostContourEditsMessage(
    action: string,
    labels: readonly string[]
): string {
    const names = labels.map((label) => `"${label}"`).join(', ');
    return labels.length === 1
        ? `${action} cancelled: contour edits to structure set ${names} ` +
              'failed to leave the editor when it was closed. Open it to ' +
              'verify its contours, then try again.'
        : `${action} cancelled: contour edits to structure sets ${names} ` +
              'failed to leave the editor when they were closed. Open them ' +
              'to verify their contours, then try again.';
}

/**
 * Writes the contour edits of every registry resident to the domain, then
 * checks that the given structure sets are current there. A set that lost
 * edits to an earlier failed switch-away flush has a stale domain copy that
 * must not be used as current: the user is told which sets, by label, and
 * false is returned.
 * A failed flush throws. With no structure set given nothing is flushed.
 *
 * @param action What the refusal cancels, as the user reads it ("Export").
 */
export function ensureStructureSetsCurrent(
    registry: Pick<DatasetEnvironmentRegistry, 'saveAllResidentChanges'>,
    structureSets: readonly LabelledStructureSet[],
    action: string
): boolean {
    if (structureSets.length === 0) {
        return true;
    }
    const lostEditIds = registry.saveAllResidentChanges();
    const lostLabels = structureSets
        .filter((ss) => lostEditIds.has(ss.id))
        .map((ss) => ss.structureSetLabel);
    if (lostLabels.length === 0) {
        return true;
    }
    enqueueSnackbar(lostContourEditsMessage(action, lostLabels), {
        variant: 'error',
    });
    return false;
}
